/**
 * summaryHelpers — shared utilities for the tooth-chart summary panel
 * and the visit-page outside chips.
 *
 * Centralises the token → scope/label projection so a quadrant ("Q1")
 * displays as "Upper-right quadrant" everywhere it appears: inside the
 * new FindingsSummary, on the visit-page chips, in print summaries.
 *
 * Token shapes accepted (matching the backend regex at
 * app/schemas/visit_metadata.py:307-333):
 *   - "11"–"48"         permanent FDI tooth
 *   - "51"–"85"         primary/deciduous FDI tooth
 *   - "Q1"–"Q4"         quadrant
 *   - "FM"              full mouth
 *   - "<token>-M|D|B|L|O"   surface suffix
 *   - "gum:<token>"     periodontal/gum scope
 */

// ── FDI → anatomical name ──────────────────────────────────────────
//
// FDI World Dental Federation notation. Quadrant is the tens digit,
// position the ones digit. Permanent dentition: 1-quadrant labels
// 11-18, 21-28, 31-38, 41-48; primary: 51-55, 61-65, 71-75, 81-85.
//
// Position labels follow common clinical usage so chairside speech and
// chart text match ("upper-right 1st molar", not "tooth 16").
const QUADRANT_LABEL = {
  1: 'Upper-right',
  2: 'Upper-left',
  3: 'Lower-left',
  4: 'Lower-right',
  // Primary dentition mirrors permanent quadrants.
  5: 'Upper-right',
  6: 'Upper-left',
  7: 'Lower-left',
  8: 'Lower-right',
};

const PERMANENT_POSITION = {
  1: 'central incisor',
  2: 'lateral incisor',
  3: 'canine',
  4: '1st premolar',
  5: '2nd premolar',
  6: '1st molar',
  7: '2nd molar',
  8: '3rd molar',
};

const PRIMARY_POSITION = {
  1: 'central incisor',
  2: 'lateral incisor',
  3: 'canine',
  4: '1st molar',
  5: '2nd molar',
};

const SURFACE_LABEL = {
  M: 'mesial surface',
  D: 'distal surface',
  B: 'buccal surface',
  L: 'lingual surface',
  O: 'occlusal surface',
};

/**
 * Build the static FDI → anatomical name map at module load. Returns
 * keys like "16" mapped to "Upper-right 1st molar". Both permanent and
 * primary dentition. ~52 entries; small and cheap.
 */
function buildFdiMap() {
  const out = {};
  // Permanent (11–48)
  for (let q = 1; q <= 4; q += 1) {
    for (let p = 1; p <= 8; p += 1) {
      out[`${q}${p}`] = `${QUADRANT_LABEL[q]} ${PERMANENT_POSITION[p]}`;
    }
  }
  // Primary (51–85)
  for (let q = 5; q <= 8; q += 1) {
    for (let p = 1; p <= 5; p += 1) {
      out[`${q}${p}`] = `${QUADRANT_LABEL[q]} ${PRIMARY_POSITION[p]} (primary)`;
    }
  }
  return out;
}

export const FDI_TO_ANATOMICAL_NAME = buildFdiMap();


// ── Token parsing ───────────────────────────────────────────────────

/**
 * Parse a token into its parts. Returns null if the shape doesn't match
 * any of the supported scopes.
 *
 *   "16"        → { scope: 'tooth',    fdi: '16', surface: null, gum: false }
 *   "16-M"      → { scope: 'surface',  fdi: '16', surface: 'M', gum: false }
 *   "Q1"        → { scope: 'quadrant', quadrant: 'Q1' }
 *   "FM"        → { scope: 'fm' }
 *   "gum:16"    → { scope: 'gum', fdi: '16', surface: null, gum: true }
 *   "gum:Q1"    → { scope: 'gum-quadrant', quadrant: 'Q1', gum: true }
 *   "gum:FM"    → { scope: 'gum-fm', gum: true }
 */
export function parseToken(token) {
  if (token == null) return null;
  let raw = String(token).trim();
  if (!raw) return null;

  let gum = false;
  if (raw.startsWith('gum:')) {
    gum = true;
    raw = raw.slice(4);
  }

  if (raw === 'FM') {
    return gum ? { scope: 'gum-fm', gum: true } : { scope: 'fm', gum: false };
  }
  if (/^Q[1-4]$/.test(raw)) {
    return gum
      ? { scope: 'gum-quadrant', quadrant: raw, gum: true }
      : { scope: 'quadrant', quadrant: raw, gum: false };
  }

  // Tooth or surface — split off the optional `-X` suffix.
  const surfaceMatch = raw.match(/^(\d{2})-([MDBLO])$/);
  if (surfaceMatch) {
    const fdi = surfaceMatch[1];
    if (!FDI_TO_ANATOMICAL_NAME[fdi]) return null;
    return {
      scope: gum ? 'gum' : 'surface',
      fdi,
      surface: surfaceMatch[2],
      gum,
    };
  }
  if (/^\d{2}$/.test(raw) && FDI_TO_ANATOMICAL_NAME[raw]) {
    return {
      scope: gum ? 'gum' : 'tooth',
      fdi: raw,
      surface: null,
      gum,
    };
  }
  return null;
}


/**
 * tokenToScope — collapse a token to one of the summary's top-level
 * section buckets:
 *
 *   'fm'        — full mouth (with or without gum: prefix)
 *   'quadrant'  — Q1–Q4 (with or without gum: prefix)
 *   'tooth'     — bare FDI without surface
 *   'surface'   — FDI with -M/-D/-B/-L/-O suffix
 *   'gum'       — periodontal scope on a specific tooth
 *
 * Returns 'unknown' for malformed tokens so callers can quietly drop
 * them rather than crashing the panel.
 */
export function tokenToScope(token) {
  const parsed = parseToken(token);
  if (!parsed) return 'unknown';
  if (parsed.scope === 'fm' || parsed.scope === 'gum-fm') return 'fm';
  if (parsed.scope === 'quadrant' || parsed.scope === 'gum-quadrant') return 'quadrant';
  if (parsed.scope === 'gum') return 'gum';
  if (parsed.scope === 'surface') return 'surface';
  if (parsed.scope === 'tooth') return 'tooth';
  return 'unknown';
}


/**
 * tokenToHumanLabel — produce the dentist-readable label for a token.
 * Examples:
 *   "16"        → "Tooth 16 · Upper-right 1st molar"
 *   "16-M"      → "Tooth 16 (mesial surface) · Upper-right 1st molar"
 *   "Q1"        → "Upper-right quadrant"
 *   "FM"        → "Full mouth"
 *   "gum:16"    → "Gum at tooth 16 · Upper-right 1st molar"
 *   "gum:FM"    → "Full mouth (gum)"
 *
 * Unparseable tokens fall through to the raw value so we never silently
 * drop them from the UI.
 */
export function tokenToHumanLabel(token, { compact = false } = {}) {
  const parsed = parseToken(token);
  if (!parsed) return String(token || '');

  if (parsed.scope === 'fm') return 'Full mouth';
  if (parsed.scope === 'gum-fm') return 'Full mouth (gum)';

  if (parsed.scope === 'quadrant' || parsed.scope === 'gum-quadrant') {
    const word = QUADRANT_LABEL[Number(parsed.quadrant.slice(1))];
    const base = `${word} quadrant`;
    return parsed.gum ? `${base} (gum)` : base;
  }

  // Tooth-level (with optional surface + gum).
  const anatomical = FDI_TO_ANATOMICAL_NAME[parsed.fdi] || '';
  const surface = parsed.surface ? ` (${SURFACE_LABEL[parsed.surface]})` : '';
  const base = parsed.gum
    ? `Gum at tooth ${parsed.fdi}${surface}`
    : `Tooth ${parsed.fdi}${surface}`;
  if (compact || !anatomical) return base;
  return `${base} · ${anatomical}`;
}


// ── Status derivation ───────────────────────────────────────────────

// Backend SittingStatus enum strings (app/schemas/treatment_plan.py:99-110)
// plus the marker-derived `'planned'` aggregate used by buildIndices.
// `in_progress` is the canonical "doctor is working on this now" state;
// the previously listed `execution_started` is NOT in the enum.
export const DONE_STATUSES = new Set(['completed', 'paid', 'closed', 'resolved']);
// IN_PROGRESS is STRICTLY the "doctor is working on this now" state. Earlier
// this set also held the pre-treatment states (scheduled / awaiting_payment /
// payment_cleared / …), which made the tooth badge read "IN PROGRESS" for a
// plan that hadn't started yet (e.g. "Payment cleared" with a Start button).
// Those now live in PLANNED_STATUSES → the amber "Planned" badge.
export const IN_PROGRESS_STATUSES = new Set([
  'in_progress',          // SittingStatus.IN_PROGRESS — the canonical live state
]);
// PLANNED — a plan exists but treatment has NOT started. Every non-terminal,
// non-active sitting state (awaiting/cleared payment, scheduled, ready, on
// hold, awaiting approval, planned aggregate). Surfaced as one coarse
// "Planned" badge; the PLAN line still shows the precise humanSittingStatus.
export const PLANNED_STATUSES = new Set([
  'scheduled',            // SittingStatus.SCHEDULED
  'awaiting_payment',     // SittingStatus.AWAITING_PAYMENT
  'payment_cleared',      // SittingStatus.PAYMENT_CLEARED
  'held',                 // SittingStatus.HELD
  'ready',                // SittingStatus.READY
  'pending_approval',     // SittingStatus.PENDING_APPROVAL
  'approved',             // approval-flow alias
  'planned',              // marker-derived aggregate (buildIndices)
]);
export const CANCELLED_STATUSES = new Set(['cancelled', 'voided', 'rejected', 'skipped']);

// Status precedence for the "most relevant" plan when several procedures
// touch one tooth. Highest priority surfaces first.
export const PLAN_STATUS_PRIORITY = {
  in_progress: 100,
  pending_approval: 90,
  ready: 80,
  payment_cleared: 75,
  scheduled: 70,
  awaiting_payment: 65,
  held: 60,
  approved: 55,
  planned: 50,
  completed: 30,
  paid: 25,
  cancelled: 0,
  voided: 0,
  rejected: 0,
  skipped: 0,
};

/**
 * isAutoGeneratedNote — true for metadata rows synthesised by the
 * backend procedure flow (patient_plan_service.py:617-631 stamps these
 * with `auto_generated: True` and a content like
 * "Composite Filling planned on 22, 23 · Doctor · 2026-05-15").
 *
 * These are useful as a chip-strip excerpt for "what just happened?"
 * timelines but they MUST NOT be displayed as a diagnosis or note in
 * the structured PROBLEM line — they're procedure prose, not clinical
 * findings.
 */
export function isAutoGeneratedNote(entry) {
  return entry?.auto_generated === true;
}


/**
 * formatRelative — "yesterday", "3d ago", "2 weeks ago", etc.
 *
 * Shared between the visit-page chip strip and the in-chart PLAN line
 * (started Xm ago). Lifted out of ToothFindingsSummary so both surfaces
 * print the same shorthand without copy-paste drift.
 *
 * Returns '' for null / unparseable input. Caller decides whether to
 * suppress the surrounding label.
 */
/**
 * Parse a clinical timestamp safely across the two formats the backend
 * has shipped historically:
 *   - bare UTC ISO (`2026-05-17T10:30:00`) — legacy sitting_service /
 *     patient_plan_service rows written before the IST-alignment fix.
 *     JS Date() would otherwise interpret these as LOCAL time, producing
 *     a 5h30m backwards drift on IST clients (the "Created 5h ago"
 *     bug for procedures that were just made).
 *   - IST with offset (`2026-05-17T16:00:00+05:30`) — visit_service and
 *     post-fix sitting writes.
 *
 * Strategy: if the string already carries a timezone marker (Z or
 * ±HH:MM), parse natively. Otherwise append 'Z' so the engine treats it
 * as UTC, which is what the legacy strings really meant.
 */
export function parseClinicalTs(iso) {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatRelative(iso) {
  const d = parseClinicalTs(iso);
  if (!d) return '';
  const ms = Date.now() - d.getTime();
  if (ms < 0) return 'just now';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const days = Math.floor(ms / (24 * 3600 * 1000));
  if (days <= 0) {
    const hours = Math.floor(ms / 3600000);
    return `${hours}h ago`;
  }
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/**
 * Absolute IST wall-clock timestamp for tooltips alongside formatRelative.
 * Output: "May 17, 2026 · 4:32 PM IST".
 */
export function formatAbsoluteIST(iso) {
  const d = parseClinicalTs(iso);
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  const date = `${get('month')} ${get('day')}, ${get('year')}`;
  const time = `${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
  return `${date} · ${time} IST`;
}


/**
 * humanSittingStatus — render a backend SittingStatus enum string in
 * the dentist's language. Used by the in-chart PLAN line and the chip
 * tooltips so both surfaces speak the same vocabulary.
 *
 * Mirrors `SittingStatus` in app/schemas/treatment_plan.py:99-110, plus
 * the marker-derived `'planned'` aggregate used by buildIndices. `paid`
 * aliases to "Completed" because patients reading the chart over the
 * dentist's shoulder shouldn't see billing jargon.
 */
export function humanSittingStatus(status) {
  switch (String(status || '').toLowerCase()) {
    case 'in_progress':       return 'In progress';
    case 'scheduled':         return 'Scheduled';
    case 'awaiting_payment':  return 'Awaiting payment';
    case 'payment_cleared':   return 'Payment cleared';
    case 'held':              return 'On hold';
    case 'ready':             return 'Ready';
    case 'pending_approval':  return 'Pending approval';
    case 'approved':          return 'Approved';
    case 'planned':           return 'Planned';
    case 'completed':         return 'Completed';
    case 'paid':              return 'Completed';
    case 'cancelled':         return 'Cancelled';
    case 'voided':            return 'Voided';
    case 'rejected':          return 'Rejected';
    case 'skipped':           return 'Skipped';
    default:                  return status ? String(status) : '';
  }
}

/**
 * deriveStatus — map a marker + its linked procedures + raw entries to
 * one of the summary's status buckets:
 *
 *   'done'        — every active procedure is complete OR the
 *                   diagnosis was explicitly marked resolved AND there
 *                   is no open plan
 *   'in_progress' — at least one linked procedure is actively being
 *                   worked (status === 'in_progress')
 *   'planned'     — a plan exists but no procedure has started yet
 *                   (scheduled / ready / awaiting_payment /
 *                   payment_cleared / held / pending_approval / …)
 *   'open'        — there is a finding (diagnosis or note) but no plan
 *                   yet, OR all linked procedures are cancelled
 */
export function deriveStatus(marker, entries = []) {
  const linked = Array.isArray(marker?.linked_procedures)
    ? marker.linked_procedures
    : [];

  const liveProcs = linked.filter((p) => {
    const s = String(p?.status || '').toLowerCase();
    return !CANCELLED_STATUSES.has(s);
  });

  if (liveProcs.length > 0) {
    const allDone = liveProcs.every((p) => DONE_STATUSES.has(
      String(p?.status || '').toLowerCase(),
    ));
    if (allDone) return 'done';
    const anyActive = liveProcs.some((p) => IN_PROGRESS_STATUSES.has(
      String(p?.status || '').toLowerCase(),
    ));
    if (anyActive) return 'in_progress';
    const anyPlanned = liveProcs.some((p) => PLANNED_STATUSES.has(
      String(p?.status || '').toLowerCase(),
    ));
    if (anyPlanned) return 'planned';
  }

  // No live procedures: either resolved-without-procedure or open.
  if (marker?.has_resolved_diagnosis && (marker?.planned_count || 0) === 0) {
    return 'done';
  }
  if (entries.length > 0 || (marker?.diagnosis_count || 0) > 0
      || (marker?.note_count || 0) > 0) {
    return 'open';
  }
  // Edge case: marker exists with no entries (e.g. terminal anatomy
  // marker). Treat as open so it still surfaces.
  return 'open';
}


// ── Display constants used by FindingsSummary ───────────────────────

export const STATUS_META = {
  open: {
    label: 'Open',
    dot: '⚪',
    // Solid colour-dot class for compact pill rows (e.g. the mobile
    // summary overlay) that show a coloured circle instead of the emoji.
    dotClass: 'bg-slate-400 dark:bg-slate-500',
    description: 'Needs a plan',
    classes: {
      light: 'bg-slate-100 text-slate-800 border-slate-300',
      dark: 'dark:bg-slate-700 dark:text-slate-100 dark:border-slate-500',
    },
  },
  planned: {
    label: 'Planned',
    dot: '🟡',
    dotClass: 'bg-amber-500',
    description: 'Plan set, not started',
    classes: {
      light: 'bg-amber-100 text-amber-800 border-amber-400',
      dark: 'dark:bg-amber-900/50 dark:text-amber-100 dark:border-amber-600',
    },
  },
  in_progress: {
    label: 'In progress',
    dot: '🟦',
    dotClass: 'bg-blue-500',
    description: 'Treatment ongoing',
    classes: {
      light: 'bg-blue-100 text-blue-800 border-blue-400',
      dark: 'dark:bg-blue-900/50 dark:text-blue-100 dark:border-blue-600',
    },
  },
  done: {
    label: 'Done',
    dot: '🟩',
    dotClass: 'bg-emerald-500',
    description: 'Treatment completed',
    classes: {
      light: 'bg-emerald-100 text-emerald-800 border-emerald-400',
      dark: 'dark:bg-emerald-900/50 dark:text-emerald-100 dark:border-emerald-600',
    },
  },
  cancelled: {
    label: 'Cancelled',
    dot: '⚫',
    dotClass: 'bg-rose-500',
    description: 'Procedure cancelled',
    // Summary-list only — a cancelled procedure never colours the tooth.
    classes: {
      light: 'bg-rose-100 text-rose-800 border-rose-400',
      dark: 'dark:bg-rose-900/50 dark:text-rose-100 dark:border-rose-600',
    },
  },
};

// Single source of truth for the per-status filter chips shown in the
// summary. BOTH the desktop StatusBanner and the mobile
// TabletChartSummaryOverlay map over this list (plus a leading `all`
// control) so the set of buckets — and their order — can never drift
// between the two UIs. Add a status to STATUS_META and it appears in
// every surface automatically.
export const STATUS_PILLS = Object.keys(STATUS_META);

export const SCOPE_META = {
  fm: { label: 'Full mouth', icon: '🦷', order: 0 },
  quadrant: { label: 'Quadrants', icon: '◳', order: 1 },
  tooth: { label: 'Individual teeth', icon: '🦷', order: 2 },
  surface: { label: 'Surfaces', icon: '🔘', order: 3 },
  gum: { label: 'Gum / Periodontal', icon: '🩸', order: 4 },
};
